import { useState } from "react";
import { Clock, Gift } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";
import {
  formatCurrency,
  loyaltyDiscountFor,
  maxRedeemablePointsFor,
  parseCurrency,
} from "@/lib/utils";
import { notify } from "@/lib/notify";
import { useCreateOrderOffline } from "@/features/orders/api";
import { useValidatePromoCode } from "@/features/settings/api";
import { useCartStore } from "@/stores";
import type { CreateOrderRequest, PaymentMethod } from "@/types";
import { LoyaltyRedemptionSection } from "./LoyaltyRedemptionSection";
import { PaymentMethodSection } from "./PaymentMethodSection";
import { PromoCodeSection } from "./PromoCodeSection";

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  total: number;
  currencySymbol: string;
  shiftId?: string;
  loyaltyEnabled: boolean;
  loyaltyRedemptionRate: number;
  loyaltyRedemptionValue: number;
  onSuccess: (
    orderId: string | null,
    isPayLater: boolean,
    isQueued: boolean,
  ) => void;
}

export function PaymentDialog({
  open,
  onOpenChange,
  total,
  currencySymbol,
  shiftId,
  loyaltyEnabled,
  loyaltyRedemptionRate,
  loyaltyRedemptionValue,
  onSuccess,
}: PaymentDialogProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  const [amountTendered, setAmountTendered] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");

  const cartStore = useCartStore();
  const createOrder = useCreateOrderOffline();
  const validatePromoCode = useValidatePromoCode();

  const customer = cartStore.customer;
  const customerPoints = customer?.loyaltyPoints || 0;
  const isProcessing = createOrder.isPending;

  const appliedPromotion = cartStore.promotion;
  const promoDiscount = cartStore.discountAmount;

  const pointsDiscount = loyaltyDiscountFor(
    pointsToRedeem,
    loyaltyRedemptionRate,
    loyaltyRedemptionValue,
  );

  const adjustedTotal = Math.max(0, total - pointsDiscount);
  const nothingLeftToCharge = adjustedTotal === 0;

  const isCoveredByPoints = nothingLeftToCharge && pointsDiscount > 0;
  const isFullyComped = nothingLeftToCharge && !isCoveredByPoints;

  const maxRedeemablePoints = Math.min(
    customerPoints,
    maxRedeemablePointsFor(total, loyaltyRedemptionRate, loyaltyRedemptionValue),
  );

  const tenderedMinorUnits = parseCurrency(amountTendered);
  const change = Math.max(0, tenderedMinorUnits - adjustedTotal);
  const cashShort =
    !nothingLeftToCharge &&
    paymentMethod === "cash" &&
    tenderedMinorUnits < adjustedTotal;

  const resetDialogState = () => {
    setAmountTendered("");
    setReferenceNumber("");
    setPointsToRedeem(0);
    setPromoCode("");
    setPromoError("");
  };

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");

      return;
    }

    setPromoError("");

    try {
      const result = await validatePromoCode.mutateAsync({
        code: promoCode.trim().toUpperCase(),
        subtotal: total,
      });

      if (!result.valid || !result.promotion) {
        setPromoError("Invalid or expired promo code");

        return;
      }

      const { promotionId, name, type, discount } = result.promotion;

      cartStore.setPromotion({ id: promotionId, name, type }, discount);
      setPromoCode("");
    } catch {
      setPromoError("Failed to validate promo code");
    }
  };

  const checkoutPayment = () => {
    if (isCoveredByPoints) {
      return { method: "loyalty" as PaymentMethod, amountTendered: 0 };
    }

    if (isFullyComped) {
      return { method: "comp" as PaymentMethod, amountTendered: 0 };
    }

    return {
      method: paymentMethod,
      amountTendered:
        paymentMethod === "cash" ? tenderedMinorUnits : adjustedTotal,
      referenceNumber: referenceNumber || undefined,
    };
  };

  const buildOrderPayload = (payLater: boolean): CreateOrderRequest => {
    const redeemsPoints = !payLater && pointsToRedeem > 0 && !!customer;

    return {
      orderType: cartStore.orderType,
      shiftId,
      customerId: cartStore.customer?.id,
      discountAmount: cartStore.discountAmount,
      promotionId: cartStore.promotion?.id,
      notes: cartStore.notes || undefined,
      loyaltyPointsToRedeem: redeemsPoints ? pointsToRedeem : undefined,
      items: cartStore.items.map((item) => ({
        variantId: item.variant.id,
        quantity: item.quantity,
        notes: item.notes || undefined,
        optionIds: item.selectedOptions.map((option) => option.id),
        isComped: item.isComped || undefined,
        compReason: item.compReason || undefined,
      })),
      ...(payLater ? {} : { payment: checkoutPayment() }),
    };
  };

  const handlePayment = async (payLater = false) => {
    try {
      const result = await createOrder.mutateAsync(buildOrderPayload(payLater));

      if (result.status === "queued") {
        onSuccess(null, payLater, true);

        return;
      }

      onSuccess(result.order.id, payLater, false);
    } catch (err) {
      notify.fromError(err, "Failed to create order");
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isProcessing) {
      return;
    }

    if (!isOpen) {
      resetDialogState();
    }

    onOpenChange(isOpen);
  };

  const showsDiscountedTotal = pointsDiscount > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-muted p-4 text-center">
            <p className="text-sm text-muted-foreground">Total Due</p>
            {showsDiscountedTotal ? (
              <>
                <p className="text-lg text-muted-foreground line-through">
                  {formatCurrency(total, currencySymbol)}
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(adjustedTotal, currencySymbol)}
                </p>
              </>
            ) : (
              <p className="text-3xl font-bold">
                {formatCurrency(total, currencySymbol)}
              </p>
            )}
          </div>
          <PromoCodeSection
            appliedPromotion={appliedPromotion}
            appliedDiscount={promoDiscount}
            promoCode={promoCode}
            promoError={promoError}
            isValidating={validatePromoCode.isPending}
            currencySymbol={currencySymbol}
            onChange={(value) => {
              setPromoCode(value);
              setPromoError("");
            }}
            onApply={handleApplyPromoCode}
            onRemove={() => {
              cartStore.clearPromotion();
              setPromoError("");
            }}
          />
          {loyaltyEnabled &&
            customer &&
            customerPoints > 0 &&
            loyaltyRedemptionRate > 0 && (
              <LoyaltyRedemptionSection
                customerPoints={customerPoints}
                loyaltyRedemptionRate={loyaltyRedemptionRate}
                loyaltyRedemptionValue={loyaltyRedemptionValue}
                pointsToRedeem={pointsToRedeem}
                maxRedeemablePoints={maxRedeemablePoints}
                pointsDiscount={pointsDiscount}
                currencySymbol={currencySymbol}
                onChange={setPointsToRedeem}
              />
            )}

          {nothingLeftToCharge ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
              <Gift className="mx-auto mb-2 h-8 w-8 text-green-600" />
              <p className="font-semibold text-green-800">
                {isCoveredByPoints ? "Paid With Points" : "Order Fully Comped"}
              </p>
              <p className="mt-1 text-sm text-green-600">
                {isCoveredByPoints
                  ? `${pointsToRedeem} points cover the whole order — nothing to collect.`
                  : "All items are complimentary — no payment required."}
              </p>
            </div>
          ) : (
            <PaymentMethodSection
              method={paymentMethod}
              onMethodChange={setPaymentMethod}
              amountTendered={amountTendered}
              onAmountChange={setAmountTendered}
              referenceNumber={referenceNumber}
              onReferenceChange={setReferenceNumber}
              adjustedTotal={adjustedTotal}
              change={change}
              currencySymbol={currencySymbol}
            />
          )}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => handlePayment(true)}
            disabled={isProcessing}
          >
            <Clock className="mr-2 h-4 w-4" />
            {isProcessing ? "Creating..." : "Pay Later"}
          </Button>
          <Button
            data-testid="confirm-payment"
            onClick={() => handlePayment(false)}
            disabled={isProcessing || cashShort}
          >
            {isProcessing
              ? "Processing..."
              : isCoveredByPoints
                ? "Confirm Points Payment"
                : isFullyComped
                  ? "Confirm Comp"
                  : `Pay ${formatCurrency(adjustedTotal, currencySymbol)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
